import React, { useState, useEffect, useRef } from "react";
import { cdnUrl } from "../../../lib/util";
import { Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import RadioWithDateSelect from "./pickUpDeliveryTime/RadioWithDateSelect";
import "animate.css/animate.css";
import httpRequest from "../../../HttpService";
import "../../NewOrder.css";
import "./DeliveryTime.css";

import CustomTooltip from "./pickUpDeliveryTime/CustomTooltip";
import AccordionNextButton from "../AccordionNextButton";
import ReturnTripSection from "./ReturnTripSection";

import Skeleton from '@mui/material/Skeleton';

const IDLE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

// Convert a picker Date to a local wall-clock "YYYY-MM-DD HH:mm" string for the
// post_quoteCustomTime @customDT*local params — the literal time the user
// picked, with no timezone math. The proc derives UTC from this server-side
// (it knows the account's timezone), and web.get_quote returns that derived
// UTC so the picker still round-trips on reload.
const toLocalWallClock = (d) => {
  if (d === null || d === undefined || d === '') return null;
  const date = d instanceof Date ? d : new Date(d);
  if (!date || isNaN(date.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const DeliveryTime = (props) => {
  const [select, setSelect] = useState(-1);
  const [deliveryTimeInfo, setDeliveryTimeInfo] = useState({readyAtDateChecked:false,deliveryTimeChecked:false});
  const [quoteTimeOptionsList, setQuoteTimeOptionsList] = useState([]);
  const [pollInterval, setPollInterval] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sectionNotification, setSectionNotification] = useState(null);
  const [isIdle, setIsIdle] = useState(false);
  const [showIdleDialog, setShowIdleDialog] = useState(false);
  const [editSettings, setEditSettings] = useState(null);
  const idleTimeoutRef = useRef(null);
  const isIdleRef = useRef(false);
  const buttonContainerRef = React.useRef(null);

  // Fetch section notification on mount
  useEffect(() => {
    httpRequest.getNotification().then((res) => {
      if (res.data && res.data.length > 0) {
        const timeOptionsNotification = res.data.find(n => n.targetSection === 'timeOptions');
        if (timeOptionsNotification && timeOptionsNotification.messageText) {
          setSectionNotification(timeOptionsNotification.messageText);
        }
      }
    }).catch((err) => {
      console.log("Error fetching notification:", err);
    });
  }, []);

  // Disable tab navigation globally when in step 4 - only allow delivery time buttons
  useEffect(() => {
    if (props.expanded !== 4) return;

    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        e.preventDefault(); // Prevent default tab behavior entirely

        // Get all delivery time option buttons that are currently tabbable
        const tabbableButtons = Array.from(
          document.querySelectorAll('.delivery-time-option-button[tabindex="0"]')
        ).filter(btn => !btn.disabled);

        if (tabbableButtons.length === 0) {
          // No buttons available, don't do anything
          return;
        }

        const currentIndex = tabbableButtons.indexOf(document.activeElement);

        if (e.shiftKey) {
          // Shift+Tab: move backwards
          if (currentIndex <= 0) {
            // Wrap to last button
            tabbableButtons[tabbableButtons.length - 1].focus();
          } else {
            tabbableButtons[currentIndex - 1].focus();
          }
        } else {
          // Tab: move forwards
          if (currentIndex === -1) {
            // Nothing focused, focus first button
            tabbableButtons[0].focus();
          } else if (currentIndex >= tabbableButtons.length - 1) {
            // Wrap to first button
            tabbableButtons[0].focus();
          } else {
            tabbableButtons[currentIndex + 1].focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [props.expanded, isLoading, props.info[4]?.isReturnTripEnabled]);

  useEffect(() => {
    props.onAlert({"target":"timeOption", "value":true});
    props.onAlert({"target":"Initial", "value":false});
  }, []);

  // Idle timeout - stop polling after 30 minutes
  useEffect(() => {
    if (props.expanded === 4 && props.completed.get(3) && !isIdle) {
      // Start/reset idle timeout when entering step 4
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
      idleTimeoutRef.current = setTimeout(() => {
        isIdleRef.current = true;
        setIsIdle(true);
        setShowIdleDialog(true);
        // Stop polling
        if (pollInterval) {
          clearInterval(pollInterval);
          setPollInterval(null);
        }
        props.updateCounterStatus(2); // Stop the countdown timer
      }, IDLE_TIMEOUT_MS);
    }

    return () => {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
    };
  }, [props.expanded, props.completed.get(3), isIdle]);

  // Reset states when leaving step 4 (but not going to step 5)
  useEffect(() => {
    if (props.expanded !== 4 && props.expanded !== 5) {
      setIsIdle(false);
      isIdleRef.current = false;
      setShowIdleDialog(false);
      setEditSettings(null);
      setDeliveryTimeInfo({readyAtDateChecked: false, deliveryTimeChecked: false});
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }
    }
  }, [props.expanded]);

  const handleContinueFromIdle = () => {
    setShowIdleDialog(false);
    setIsIdle(false);
    isIdleRef.current = false;
    // Refresh time options and restart polling
    getQuoteTimeOptions();
    resetPollInterval();
  };

  const resetPollInterval = () => {
    clearInterval(pollInterval);
    const interval = setInterval(() => {
      if (isIdleRef.current) return; // Don't update if idle
      props.updateCounterStatus(0);
      if(props.completed.get(3) && !props.completed.get(5))
        getQuoteTimeOptions();
    }, 90000);
    setPollInterval(interval);
  }

  useEffect(() => {
    let timeoutId;

    // Only setup polling when in section 4 and completed section 3
    if (props.expanded === 4 && props.completed.get(3)) {
      // Restore previous state if it exists in props.info[4]
      if (props.info[4]) {
        const savedInfo = props.info[4];

        // Restore radio button states
        const restoredDeliveryTimeInfo = {
          readyAtDateChecked: savedInfo.readyAtDateChecked || false,
          deliveryTimeChecked: savedInfo.deliveryTimeChecked || false,
          readyAtDate: savedInfo.readyAtDate || null,
          deliveryTime: savedInfo.deliveryTime || null
        };
        setDeliveryTimeInfo(restoredDeliveryTimeInfo);

        // Restore selected time option
        if (savedInfo.timeOption && savedInfo.timeOption.timeOptionID) {
          setSelect(savedInfo.timeOption.timeOptionID);
        } else {
          setSelect(-1);
        }
      } else {
        setDeliveryTimeInfo({readyAtDateChecked:false,deliveryTimeChecked:false});
        setSelect(-1);
      }

      // SOLUTION 1: Removed immediate call to getQuoteTimeOptions() and using only the timeout
      // This prevents the function from being called twice
      timeoutId = setTimeout(() => {
        getQuoteTimeOptions();
      }, 500);

      // Setup polling interval
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      const interval = setInterval(() => {
        if (isIdleRef.current) return; // Don't update if idle
        if (props.expanded === 4) { // Only run if still in section 4
          props.updateCounterStatus(0);
          if (props.completed.get(3) && !props.completed.get(5)) {
            getQuoteTimeOptions();
          }
        }
      }, 90000);

      setPollInterval(interval);
    } else {
      // Clean up timers when not in section 4
      if (pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
      // Only stop countdown timer if leaving both steps 4 and 5 (not when going to step 5)
      if (props.expanded !== 5) {
        props.updateCounterStatus(2); // Signal timer completion
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
    };
  }, [props.expanded, props.completed.get(3)]);

  useEffect(() => {
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
    };
  }, []);

  const getQuoteTimeOptions = () => {
    setIsLoading(true);
    props.updateCounterStatus(0);

    // Store current selection before fetching new options
    const currentlySelected = props.info[4]?.timeOption?.timeOptionID || null;

    httpRequest.getQuoteTimeOptions(props.info[0].quoteId).then((res) => {
      console.log("getQuoteTimeOptions", JSON.stringify(res.data));

      // Handle new response format with timeOptions and editSettings
      const timeOptions = res.data?.timeOptions || res.data;
      const settings = res.data?.editSettings || null;

      console.log(`🕐 getQuoteTimeOptions response editSettings:`, settings);

      if (settings) {
        setEditSettings(settings);
      }

      if(timeOptions?.length === 1 && timeOptions[0].optionText === undefined) return;
      setQuoteTimeOptionsList(timeOptions);

      // If there was a previous selection, try to restore it
      if (currentlySelected !== null) {
        const stillExists = timeOptions.find(opt => opt.timeOptionID === currentlySelected);
        if (stillExists) {
          // Keep the selection but UPDATE the stored object with fresh data
          // This ensures quoteTimeOptionID is current (IDs are regenerated on each API call)
          setSelect(currentlySelected);
          props.onChange({"target": "timeOption", "value": stillExists});
        } else {
          // Option no longer exists, clear selection
          setSelect(-1);
          props.onAlert({"target": "timeOption", "value": true});
          props.onChange({"target": "timeOption", "value": null});
        }
      } else {
        // No previous selection
        setSelect(-1);
        props.onAlert({"target": "timeOption", "value": true});
        props.onChange({"target": "timeOption", "value": null});
      }

      props.updateCounterStatus(1);
    }).catch((err) => {
      // Without this, a backend 500 here becomes an unhandled rejection that
      // triggers CRA's runtime error overlay and blanks the whole page on
      // quote load (this runs on mount).
      console.error("getQuoteTimeOptions failed", err);
      props.updateCounterStatus(1);
    }).finally(() => {
      setIsLoading(false);
    });
  }

  const handleRefreshClick = () => {
    getQuoteTimeOptions();
    resetPollInterval();  
  };

  const handleClick = (index) => (event) => {
    props.onAlert({"target": "timeOption", "value": false});
    props.onChange({"target": "timeOption", "value": quoteTimeOptionsList.find(e => e.timeOptionID === index)});
    setSelect(index);     
  };

  useEffect(() => {
    console.log("deliveryTimeInfo", JSON.stringify(deliveryTimeInfo));
  }, [deliveryTimeInfo.readyAtDate, deliveryTimeInfo.deliveryTime]);

  const handleChange = (event) => {
    console.log(`${event.target.name}--${event.target.value}`);
    const updatedInfo = {...deliveryTimeInfo, [event.target.name]: event.target.value};
    setDeliveryTimeInfo(updatedInfo);

    // Save state to props so it persists when navigating away and back
    props.onChange({"target": event.target.name, "value": event.target.value});

    // Common handlers for the postQuoteCustomTime promise. The .catch is
    // important — without it, a backend 500 becomes an unhandled rejection
    // that triggers CRA's runtime error overlay and blanks the page.
    const onCustomTimeSuccess = (res) => {
      console.log("postQuoteCustomTime", JSON.stringify(res));
      getQuoteTimeOptions();
      resetPollInterval();
    };
    const onCustomTimeError = (err) => {
      console.error("postQuoteCustomTime failed", err);
      // Keep the polling loop alive so transient backend failures self-recover.
      resetPollInterval();
    };

    if(event.target.name === 'readyAtDate') {
      clearInterval(pollInterval);
      const pickup = event.target.value;
      const delivery = deliveryTimeInfo.deliveryTimeChecked? deliveryTimeInfo.deliveryTime: null;
      httpRequest.postQuoteCustomTime(
        props.info[0].quoteId,
        toLocalWallClock(pickup),
        toLocalWallClock(delivery)
      ).then(onCustomTimeSuccess).catch(onCustomTimeError);
    }
    if(event.target.name === 'deliveryTime' ) {
      clearInterval(pollInterval);
      const pickup = deliveryTimeInfo.readyAtDateChecked? deliveryTimeInfo.readyAtDate: new Date();
      const delivery = event.target.value;
      httpRequest.postQuoteCustomTime(
        props.info[0].quoteId,
        toLocalWallClock(pickup),
        toLocalWallClock(delivery)
      ).then(onCustomTimeSuccess).catch(onCustomTimeError);
    }
    if(event.target.name === 'readyAtDateChecked' && !event.target.value && deliveryTimeInfo.readyAtDate) {
      clearInterval(pollInterval);
      setDeliveryTimeInfo(oldValues => ({...oldValues, readyAtDate: null}));
      props.onChange({"target": "readyAtDate", "value": null});
      const pickup = new Date();
      const delivery = deliveryTimeInfo.deliveryTimeChecked? deliveryTimeInfo.deliveryTime: null;
      httpRequest.postQuoteCustomTime(
        props.info[0].quoteId,
        toLocalWallClock(pickup),
        toLocalWallClock(delivery)
      ).then(onCustomTimeSuccess).catch(onCustomTimeError);
    }
    if(event.target.name === 'deliveryTimeChecked' && !event.target.value && deliveryTimeInfo.deliveryTime) {
      clearInterval(pollInterval);
      setDeliveryTimeInfo(oldValues => ({...oldValues, deliveryTime: null}));
      props.onChange({"target": "deliveryTime", "value": null});
      const pickup = deliveryTimeInfo.readyAtDateChecked? deliveryTimeInfo.readyAtDate: new Date();
      httpRequest.postQuoteCustomTime(
        props.info[0].quoteId,
        toLocalWallClock(pickup),
        null
      ).then(onCustomTimeSuccess).catch(onCustomTimeError);
    }
  };

  const handleNextPanel = () => {    
    props.onNextPanel();
  };

  return (
    <div className="delivery-time-container">
      {/* Section Notification Alert */}
      {sectionNotification && (
        <div className="delivery-time-notification">
          <div className="delivery-time-notification-box">
            <WarningIcon className="delivery-time-notification-icon" />
            <Typography variant="h6" className="delivery-time-notification-text">
              {sectionNotification}
            </Typography>
          </div>
        </div>
      )}

      {/* Radio Options Row */}
      <div className="delivery-time-options-row">
        {(!editSettings || editSettings.dtDeliveryStartEdit !== 'hidden') ? (
          <div className="delivery-time-radio-section">
            <RadioWithDateSelect
              header="Pick Up in Future?"
              optionOne="No"
              optionTwo="Yes"
              onChange={handleChange}
              name="readyAtDate"
              value={deliveryTimeInfo.readyAtDateChecked!==undefined?deliveryTimeInfo.readyAtDateChecked:false}
              tabIndex={-1}
              initialOpen={editSettings?.dtDeliveryStartEdit === 'open'}
              initialDate={deliveryTimeInfo.readyAtDate || null}
            />
          </div>
        ) : (!editSettings || editSettings.dtDeliveryEndEdit !== 'hidden') ? (
          <div className="delivery-time-radio-section" />
        ) : null}
        {(!editSettings || editSettings.dtDeliveryEndEdit !== 'hidden') && (
          <div className="delivery-time-radio-with-refresh">
            <div className="delivery-time-radio-wrapper">
              <RadioWithDateSelect
                header='Specify Delivery By Time?'
                optionOne='No'
                optionTwo='Yes'
                onChange={handleChange}
                tabIndex={-1}
                name="deliveryTime"
                value={deliveryTimeInfo.deliveryTimeChecked!==undefined?deliveryTimeInfo.deliveryTimeChecked:false}
                initialOpen={editSettings?.dtDeliveryEndEdit === 'open'}
                initialDate={deliveryTimeInfo.deliveryTime || null}
              />
            </div>
          {props.info[0].Internal && (
            <div className="delivery-time-refresh-wrapper">
              <Button
                variant='contained'
                color='primary'
                className="delivery-time-refresh-btn"
                disabled={pollInterval === null}
                onClick={handleRefreshClick}
                tabIndex={-1}
              >
                Refresh
              </Button>
            </div>
          )}
          </div>
        )}
      </div>

      {/* Quick Select Header */}
      <div className="delivery-time-section-header">
        <Typography variant="h6" color="primary">
          Quick Select
        </Typography>
      </div>
      <div ref={buttonContainerRef} className="delivery-time-cards-container">
      {isLoading ? (
        <>
          {[1,2,3].map((n) => (
            <div className="delivery-time-card-wrapper" key={n}>
              <Skeleton variant="rounded" height={200} style={{ width: '100%', maxWidth: 500 }} />
            </div>
          ))}
        </>
      ) : (
        quoteTimeOptionsList.map((item, index) => {
          return (
            <div className="delivery-time-card-wrapper" key={item.timeOptionID}>
              <Button
                variant="outlined"
                size="large"
                disabled={item.optionDisabled}
                onClick={handleClick(item.timeOptionID)}
                className={`delivery-time-option-button delivery-time-card ${select === item.timeOptionID ? 'selected' : ''}`}
                tabIndex={props.expanded === 4 && !props.info[4]?.isReturnTripEnabled ? 0 : -1}
              >
                <div className="delivery-time-card-content">
                  <div className="delivery-time-card-header">
                    <div className="delivery-time-card-icon">
                      <img src={cdnUrl(item.optionIconURL)} alt={item.optionText || 'option'} />
                    </div>
                    <div className="delivery-time-card-text">
                      <Typography variant="h6" className="delivery-time-card-title">
                        {item.optionText}
                      </Typography>
                      <Typography variant="body1" className="delivery-time-card-subtext">
                        {item.optionSubText}
                      </Typography>
                      <Typography variant="body1" className="delivery-time-card-line">
                        {item.optionLine1Text}
                      </Typography>
                      <Typography variant="body1" className="delivery-time-card-line">
                        {item.optionLine2Text}
                      </Typography>
                    </div>
                  </div>

                  <Divider />

                  <div className="delivery-time-card-footer">
                    <div className="delivery-time-card-special">
                      {item.optionSpecialText && (
                        <>
                          <img src="https://cdn.directx.ca/icon/warning.png" alt="warning" />
                          <span>{item.optionSpecialText}</span>
                        </>
                      )}
                    </div>
                    <div className="delivery-time-card-price">
                      {item.showInfo === 1 && (
                        <CustomTooltip content={item.infoText}>
                          <IconButton size="small" className="ml-1" tabIndex={-1}>
                            <InfoIcon color="primary" fontSize="small" />
                          </IconButton>
                        </CustomTooltip>
                      )}
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'CAD' }).format(item.RATE)}
                    </div>
                  </div>
                </div>
              </Button>
            </div>
          )
        })
      )}
      </div>

      {/* Return Trip Section */}
      <div className="delivery-time-return-trip-wrapper">
        <Divider />
        <ReturnTripSection
          info={props.info}
          expanded={props.expanded}
          onAlert={props.onAlert}
          onChange={props.onChange}
          onNextPanel={props.onNextPanel}
          updateCountTimerStatus={props.updateCounterStatus}
        />
      </div>

      {/* Next Button */}
      <div className="delivery-time-next-wrapper">
        <AccordionNextButton
          onClick={handleNextPanel}
          disabled={select === -1 || isIdle}
          panelIndex={4}
          activePanel={props.expanded}
          errorMessage={select === -1 ? 'Please select a delivery time option.' : ''}
        />
      </div>

      {/* Idle Timeout Dialog */}
      <Dialog open={showIdleDialog} disableEscapeKeyDown>
        <DialogTitle>Are you still there?</DialogTitle>
        <DialogContent>
          <Typography>
            This page has been idle for a while. Click continue to refresh the delivery options and resume.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleContinueFromIdle} color="primary" variant="contained">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DeliveryTime;